import React, { useEffect, useState } from "react";
import {
  useSelectedHeight,
  useSelectedColor,
  HIGH_HEIGHT_UPCHARGE,
} from "@/hooks/usePillowOptions";
import { Star } from "lucide-react";

// Bundle prices mirror BUNDLES from /data/landing.js
const BUNDLE_PRICES = { 1: 147, 2: 187, 4: 297 };

/**
 * Sticky desktop checkout bar. Appears below the nav after scrolling past
 * the hero. Mirrors current size/color/bundle state and routes to ShopZone.
 * Hidden on mobile (StickyMobileCta handles that).
 */
export default function StickyDesktopCart() {
  const height = useSelectedHeight();
  const color = useSelectedColor();
  const [show, setShow] = useState(false);
  const [qty, setQty] = useState(1);

  // Re-read bundle selection from a custom event ShopZone could fire (qty)
  useEffect(() => {
    const onQty = (e) => {
      if (typeof e.detail === "number" && BUNDLE_PRICES[e.detail]) {
        setQty(e.detail);
      }
    };
    window.addEventListener("dp:qty-change", onQty);
    return () => window.removeEventListener("dp:qty-change", onQty);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      // Show after user passes ~Hero height (~900px) but hide near footer
      const y = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setShow(y > 900 && y < max - 600);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const total =
    BUNDLE_PRICES[qty] + (height === "High" ? HIGH_HEIGHT_UPCHARGE * qty : 0);

  const scrollToShop = () =>
    document.getElementById("shop")?.scrollIntoView({ behavior: "smooth" });

  if (!show) return null;

  return (
    <div
      data-testid="sticky-desktop-cart"
      className="hidden md:flex fixed bottom-4 left-1/2 -translate-x-1/2 z-40 items-center gap-4 bg-[#0e0e10] text-white border border-white/10 rounded-full shadow-2xl px-3 py-2 fade-in-up"
      style={{ maxWidth: "min(720px, calc(100% - 24px))" }}
    >
      <div className="w-10 h-10 bg-[#f5f4f1] rounded-full overflow-hidden shrink-0">
        <img
          src="/assets/Almohada_1_Mejorada.png"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      <div className="flex flex-col leading-tight pr-2 min-w-0">
        <div className="text-[12px] font-extrabold text-[#0e0e10] truncate">
          Dubai Pillow ·{" "}
          <span className="text-[#6b6b72] font-semibold">
            {qty}× {color} · {height} loft
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-[#6b6b72]">
          <div className="flex">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star
                key={i}
                size={9}
                className="fill-[#d8f06b] text-[#d8f06b]"
              />
            ))}
          </div>
          <span className="font-bold text-[#0e0e10]">4.9</span>
          <span>· 2,847 reviews</span>
        </div>
      </div>
      <div className="text-right pr-1 hidden lg:block">
        <div className="text-[10px] uppercase tracking-wider text-[#8a8a8a] font-bold">
          Total
        </div>
        <div className="text-[16px] font-black text-[#0e0e10] tabular-nums leading-none">
          ${total}
        </div>
      </div>
      <button
        type="button"
        onClick={scrollToShop}
        data-testid="sticky-desktop-cart-cta"
        className="bg-[#0e0e10] text-white text-[12px] font-extrabold uppercase tracking-wider rounded-full px-5 py-2.5 hover:bg-[#d8f06b] hover:text-[#0e0e10] transition shrink-0"
      >
        See it · ${total} →
      </button>
    </div>
  );
}
